// modules
import { flow, get, isEmpty, sortBy, uniqBy } from 'lodash'
import { createSelector } from 'reselect'

import { clinicianUsers$ } from 'src/store/modules/actions/telehealth'
import { caregiverList$ } from 'src/store/modules/admin'
// selectors
import {
  companyCaseManagersSelector,
  companyDiseasesSelector,
  companyHierarchySelector,
  companyPatientGroupsSelector,
  patientInsuranceTypes$
} from 'src/store/modules/company'
import {
  backfillCarePlan$,
  backfillMedication$,
  companyActivityTypesSelector,
  companyCheckInRefSelector,
  companyEducationRefSelector,
  companyMeasurementRefSelector,
  companyTelemedicineRefs$
} from 'src/store/modules/company/refs'

// constants
import {
  NO_CAREGIVER_DROPDOWN_VALUE,
  NO_CARE_TEAM_DROPDOWN_VALUE,
  NO_GROUP_DROPDOWN_VALUE,
  NO_INSHURANCE_DROPDOWN_VALUE,
  NO_PROBLEM_DROPDOWN_VALUE,
  TCM_CODES
} from 'src/constants'
// utils
import {
  makeOneLevelArray,
  mapBillingCodes,
  mapBillingCodesByCode
} from 'src/utils/Array'
import {
  addAllValue,
  addSelectAllValue,
  mapDataHierarchy
} from 'src/utils/DataTrees'
import {
  addAllValue as addAllValueV5,
  convertDataFields
} from 'src/utils/v5/DataTrees'

import { userPermissions$ } from '../user'

const common = (state = {}) => state?.common || {}

const activityDashboard = (state = {}) =>
  state?.company?.activityDashboard || {}

const telemedicineDashboard = (state = {}) =>
  state?.dashboard?.telemedicineDashboard || {}

const companyProviders$ = (state = {}) =>
  sortBy(state?.user?.profile?.providers || [], ({ name }) =>
    name.toLowerCase()
  )

const gamificationModules$ = (state = {}) =>
  state?.patient?.gamification?.gamificationModules || []

const gamificationAchievements$ = (state = {}) =>
  state?.patient?.gamification?.gamificationAchievements || []

export const patientAddActivityBillingCodeList$ = createSelector(
  common,
  ({ patientAddActivityBillingCodeList = {} }) =>
    patientAddActivityBillingCodeList
)

export const billingCodeList$ = (listName) =>
  createSelector(common, (items) => {
    const billingCodeList = get(items, listName, '')
    const mappedCodes = []

    delete billingCodeList.teleMedicine

    Object.keys(billingCodeList).forEach((key) => {
      Object.keys(billingCodeList[key]).forEach((uuid, idx) => {
        const code = {
          uuid,
          ...billingCodeList[key][uuid]
        }
        code.description =
          code.addOn && !isEmpty(code.addOn)
            ? `${code.code} - ${code.description} / ${code.addOn
                .map((addOn) => `${addOn.code} - ${addOn.description}`)
                .join(' / ')}`
            : `${code.code} - ${code.description}`

        if (!idx)
          mappedCodes.push({
            description: key.toUpperCase(),
            disabled: true,
            key
          })

        mappedCodes.push(code)
      })
    })

    return mappedCodes
  })

export const allBillingCodes$ = createSelector(
  activityDashboard,
  telemedicineDashboard,
  ({ billingCodes = [] }, { telemedicineBillingCodes = [] }) =>
    mapBillingCodes([...billingCodes, ...telemedicineBillingCodes])
)

export const allBillingCodesWithAll$ = createSelector(
  allBillingCodes$,
  (allBillingCodes = []) => addAllValue(allBillingCodes)
)

export const allBillingCodesWithAllV5$ = createSelector(
  allBillingCodes$,
  (allBillingCodes = []) =>
    addAllValueV5({ list: allBillingCodes, convertList: true })
)

export const allBillingCodesByCodelPatientsActivityLogWithAll$ = createSelector(
  activityDashboard,
  telemedicineDashboard,
  ({ billingCodes = [] }, { telemedicineBillingCodes = [] }) =>
    addAllValue(
      mapBillingCodesByCode([...billingCodes, ...telemedicineBillingCodes])
    )
)

export const allBillingCodesByCodelWithAll$ = createSelector(
  activityDashboard,
  telemedicineDashboard,
  ({ billingCodes = [] }, { telemedicineBillingCodes = [] }) => {
    const codes = addAllValue(
      mapBillingCodesByCode(
        makeOneLevelArray([
          ...billingCodes,
          ...telemedicineBillingCodes
        ]).filter(({ code }) => code !== 'TCM'),
        false
      )
    )
    const updatedData = codes?.map((group) => ({
      ...group,
      children: group.children
        .map((item) =>
          item.value === '99454'
            ? {
                ...item,
                title: '99454 - Collection/Transmission RPM >=16',
                label: '99454 - Collection/Transmission RPM >=16'
              }
            : item
        )
        .concat(
          TCM_CODES.filter(
            (code) => !group.children.some(({ value }) => value === code)
          ).map((code) => ({
            key: code,
            value: code,
            title:
              code === '99495'
                ? '99495 - TCM Moderate Complexity'
                : '99496 - TCM High Complexity',
            label:
              code === '99495'
                ? '99495 - TCM Moderate Complexity'
                : '99496 - TCM High Complexity'
          }))
        )
    }))

    return updatedData
  }
)

export const companyCaseManagersWithAll$ = createSelector(
  companyCaseManagersSelector,
  (caseManagers = []) => addAllValue(caseManagers)
)

export const companyCaseManagersWithAllWithNoCaseManager$ = createSelector(
  companyCaseManagersSelector,
  (caseManagers = []) =>
    addAllValue([
      ...caseManagers,
      { value: 'no-case-manager', title: 'No Case Manager' }
    ])
)

export const companyGroupsDropdown$ = createSelector(
  companyPatientGroupsSelector,
  (groups = []) =>
    groups
      .filter(({ enabled }) => enabled)
      .map(({ uuid, refValue }) => ({
        key: uuid,
        label: refValue,
        title: refValue,
        value: uuid
      }))
)

export const companyGroupsAndNoDropdown$ = createSelector(
  companyPatientGroupsSelector,
  (groups = []) => [
    ...groups
      .filter(({ enabled }) => enabled)
      .map(({ uuid, refValue }) => ({
        key: uuid,
        title: refValue,
        label: refValue,
        value: uuid
      })),
    NO_GROUP_DROPDOWN_VALUE
  ]
)

export const companyGroupsWithAll$ = createSelector(
  companyPatientGroupsSelector,
  (groups = []) => addAllValue(groups, true)
)

export const companyGroupsWithAllAndNoGroup$ = createSelector(
  companyPatientGroupsSelector,
  (groups = []) => addAllValue([...groups, NO_GROUP_DROPDOWN_VALUE], true)
)

export const companyGroupsWithAllAndNoGroupV5$ = createSelector(
  companyPatientGroupsSelector,
  (groups = []) =>
    addAllValueV5({
      list: [...groups, NO_GROUP_DROPDOWN_VALUE]
    })
)

export const companyHierarchyRootUuid$ = createSelector(
  companyHierarchySelector,
  (companyHierarchyTree = []) => companyHierarchyTree?.[0]?.uuid
)

export const companyHierarchyTree$ = createSelector(
  companyHierarchySelector,
  (companyHierarchyTree = []) => convertDataFields(companyHierarchyTree)
)

export const companyHierarchyTreeActive$ = createSelector(
  companyHierarchySelector,
  (companyHierarchyTree = []) =>
    mapDataHierarchy(companyHierarchyTree, 'children', false, true)
)

export const companyHierarchyTreeWithAllAndDisabled$ = createSelector(
  companyHierarchySelector,
  (companyHierarchyTree = []) =>
    mapDataHierarchy(companyHierarchyTree, 'children', true)
)

export const companyHierarchyTreeActiveWithAllAndDisabled$ = createSelector(
  companyHierarchySelector,
  (companyHierarchyTree = []) =>
    mapDataHierarchy(companyHierarchyTree, 'children', true, true)
)

export const providerTypeEnabledDropdown$ = createSelector(
  companyProviders$,
  (providerTypes = []) =>
    providerTypes
      .filter(({ isActive = false }) => isActive)
      .map(({ uuid, name }) => ({
        key: uuid,
        value: uuid,
        title: name
      }))
)

export const providerTypesWithAll$ = createSelector(
  companyProviders$,
  (providerTypes = []) => addAllValue(providerTypes, true)
)

export const providerTypesWithAllV5$ = createSelector(
  companyProviders$,
  (providerTypes = []) => addAllValueV5({ list: providerTypes })
)

export const caregiverWithAll$ = createSelector(
  caregiverList$,
  (caregiverList = []) => addAllValue(caregiverList, true)
)

export const caregiverWithAllAndNoCaregiver$ = createSelector(
  caregiverList$,
  (caregiverList = []) =>
    addAllValue([...caregiverList, NO_CAREGIVER_DROPDOWN_VALUE], true)
)

export const caregiverWithAllAndNoCaregiverV5$ = createSelector(
  caregiverList$,
  (caregiverList = []) =>
    addAllValueV5({ list: [...caregiverList, NO_CAREGIVER_DROPDOWN_VALUE] })
)

export const medicalDeviceTypes$ = createSelector(common, (common = {}) =>
  get(common, ['medicalDeviceTypes'], [])
)

export const medicalDeviceTypesDropdown$ = createSelector(
  medicalDeviceTypes$,
  (medicalDeviceTypes = []) =>
    sortBy(
      medicalDeviceTypes.map((item) => ({
        key: item.uuid,
        title: item.name,
        value: item.uuid,
        isActive: item.isActive,
        isIntegrationIdRequired: item.isIntegrationIdRequired
      })),
      'orderNumber'
    )
)

export const medicalDeviceTypesWithAll$ = createSelector(
  medicalDeviceTypesDropdown$,
  (medicalDeviceTypes = []) => addAllValue(medicalDeviceTypes, false)
)

export const clinicianUsersWithAll$ = createSelector(
  clinicianUsers$,
  (clinicianUsers = []) =>
    flow(
      (items = []) =>
        items.map((item, key) => ({
          ...item,
          key,
          title: `${item.fullName}${item.enabled ? '' : ' (Inactive)'}`,
          value: item.uuid
        })),
      (items = []) => addAllValue(items, true)
    )(clinicianUsers)
)

export const telemedicineBillingCodesWithSelectAll$ = createSelector(
  telemedicineDashboard,
  ({ telemedicineBillingCodes = [] }) =>
    addSelectAllValue(mapBillingCodes(telemedicineBillingCodes))
)

export const gamificationModulesTreeWithAll$ = createSelector(
  gamificationModules$,
  (gamificationModules = []) =>
    flow(
      (modules = []) =>
        modules.map((mod) => {
          let value = mod.uuid

          modules.forEach((module) => {
            if (module.module === mod.module && module.uuid !== mod.uuid)
              value = `${value},${module.uuid}`
          })

          const title = mod.module.replace('_', ' ').toLowerCase()

          return {
            value,
            key: value,
            title: `${title.charAt(0).toUpperCase()}${title.substring(1)}`
          }
        }),
      (modules = []) => sortBy(modules, ({ title }) => title),
      (modules = []) => uniqBy(modules, ({ title }) => title),
      (modules = []) => addAllValue(modules, false)
    )(gamificationModules)
)

export const gamificationAchievementsTreeWithAll$ = createSelector(
  gamificationAchievements$,
  (gamificationAchievements = []) => {
    const updGamificationAchievements = sortBy(
      gamificationAchievements.map(({ uuid, title: { English } }) => ({
        value: uuid,
        key: uuid,
        title: English
      })),
      ({ title }) => title
    )

    return addAllValue(updGamificationAchievements, false)
  }
)

export const patientInsuranceTypesWithAll$ = createSelector(
  patientInsuranceTypes$,
  (patientInsuranceTypes = []) =>
    addAllValue(mapDataHierarchy(patientInsuranceTypes))
)

export const patientInsuranceTypesWithAllAndNoGroup$ = createSelector(
  patientInsuranceTypes$,
  (patientInsuranceTypes = []) =>
    addAllValue(
      [
        ...mapDataHierarchy(patientInsuranceTypes),
        NO_INSHURANCE_DROPDOWN_VALUE
      ],
      true,
      'All'
    )
)

export const minimizedModals$ = createSelector(
  common,
  ({ minimizedModals }) => minimizedModals
)

export const activityReasonTypes$ = createSelector(
  companyActivityTypesSelector,
  companyEducationRefSelector,
  companyCheckInRefSelector,
  backfillCarePlan$,
  companyMeasurementRefSelector,
  companyTelemedicineRefs$,
  backfillMedication$,
  (
    activityReasons = [],
    educationRef = [],
    checkInRef = [],
    backfillCarePlanRef = [],
    measurementRef = [],
    telemedicineRefs = [],
    backfillMedication = []
  ) =>
    flow(
      (items = []) =>
        items.map(({ uuid, refValue }) => ({
          key: uuid,
          value: uuid,
          title: refValue
        })),
      (items = []) => sortBy(items, 'title')
    )([
      ...activityReasons,
      ...educationRef,
      ...checkInRef,
      ...backfillCarePlanRef,
      ...measurementRef,
      ...telemedicineRefs,
      ...backfillMedication
    ])
)

export const activityReasonTypesWithAll$ = createSelector(
  activityReasonTypes$,
  (items = []) => addAllValue(items)
)

export const activityReasonTypesWithAllV5$ = createSelector(
  activityReasonTypes$,
  (items = []) => addAllValueV5({ list: items, convertList: true })
)

export const companyDiseasesWithAll$ = createSelector(
  companyDiseasesSelector,
  (diseases = []) => addAllValue(diseases, true)
)

export const companyDiseasesWithAllAndNoProblem$ = createSelector(
  companyDiseasesSelector,
  (diseases = []) => addAllValue([...diseases, NO_PROBLEM_DROPDOWN_VALUE], true)
)

export const commonAgencyCaseManagementList$ = createSelector(
  common,
  ({ agencyCaseManagementList = [] }) => agencyCaseManagementList
)

export const commonAgencyCaseManagementListDropdown$ = createSelector(
  commonAgencyCaseManagementList$,
  (agencyCaseManagementList = []) =>
    agencyCaseManagementList.map((item) => ({
      key: item.uuid,
      title: `${item.firstName} ${item.lastName}`,
      label: `${item.firstName} ${item.lastName}`,
      value: item.uuid
    }))
)

export const urgentCareOutcomeModalVisible$ = createSelector(
  common,
  ({ urgentCareOutcomeModalVisible = false }) => urgentCareOutcomeModalVisible
)

export const urgentCareOutcomeModalInitialValue$ = createSelector(
  common,
  ({ urgentCareOutcomeModalInitialValue = {} }) =>
    urgentCareOutcomeModalInitialValue
)

export const urgentCareOutcomeModalSaved$ = createSelector(
  common,
  ({ urgentCareOutcomeModalSaved = {} }) => urgentCareOutcomeModalSaved
)

export const urgentCareCallUuid$ = createSelector(
  common,
  ({ urgentCareCallUuid = null }) => urgentCareCallUuid
)

export const transcriptionModalVisible$ = createSelector(
  common,
  ({ transcriptionModalVisible = false }) => transcriptionModalVisible
)

export const transcriptionModalInitialValue$ = createSelector(
  common,
  ({ transcriptionModalInitialValue = {} }) => transcriptionModalInitialValue
)

export const transcriptionModalSaved$ = createSelector(
  common,
  ({ transcriptionModalSaved = false }) => transcriptionModalSaved
)

export const telemedicineOutcomeModalInitialValue$ = createSelector(
  common,
  ({ telemedicineOutcomeModalInitialValue = {} }) =>
    telemedicineOutcomeModalInitialValue
)

export const careTeams$ = createSelector(
  common,
  ({ careTeams = [] }) => careTeams
)

export const careTeamsDropdown$ = createSelector(careTeams$, (careTeams = []) =>
  careTeams.map((item) => ({
    ...item,
    key: item?.uuid,
    title: item?.name,
    label: item?.name,
    value: item?.uuid
  }))
)

export const careTeamsWithAll$ = createSelector(
  careTeamsDropdown$,
  (careTeams = []) => addAllValue(careTeams)
)

export const careTeamsWithAllAndNoCareTeam$ = createSelector(
  careTeamsDropdown$,
  (careTeams = []) => addAllValue([...careTeams, NO_CARE_TEAM_DROPDOWN_VALUE])
)

export const activityModalOpen$ = createSelector(
  common,
  ({ activityModalOpen = false }) => activityModalOpen
)

export const activityClinicalEventsModalOpen$ = createSelector(
  common,
  ({ activityClinicalEventsModalOpen = false }) =>
    activityClinicalEventsModalOpen
)

export const activityClinicalEventsModalPatient$ = createSelector(
  common,
  ({ activityClinicalEventsModalPatient = '' }) =>
    activityClinicalEventsModalPatient
)

export const activityModalInitialValues$ = createSelector(
  common,
  ({ activityModalInitialValues = {} }) => activityModalInitialValues
)

export const activityModalIsSuccessed$ = createSelector(
  common,
  ({ activityModalIsSuccessed = false }) => activityModalIsSuccessed
)

export const isCommunicationActive$ = createSelector(
  userPermissions$,
  (userPermissions = []) => !userPermissions?.communication?.permissions?.none
)

export const activityTimerModalOpen$ = createSelector(
  common,
  ({ activityTimerModalOpen = false }) => activityTimerModalOpen
)

export const activityTimerModalInitialValues$ = createSelector(
  common,
  ({ activityTimerModalInitialValues = {} }) => activityTimerModalInitialValues
)

export const activityTimerModalInitialValuesPatient$ = createSelector(
  activityTimerModalInitialValues$,
  (activityTimerModalInitialValues = {}) =>
    activityTimerModalInitialValues?.patient?.uuid || null
)

export const activityTimerModalIsSuccessed$ = createSelector(
  common,
  ({ activityTimerModalIsSuccessed = false }) => activityTimerModalIsSuccessed
)

export const activityTimerRecording$ = createSelector(
  common,
  ({ activityTimerRecording = false }) => activityTimerRecording
)

export const activityTimerExtra$ = createSelector(
  common,
  ({ timerExtra = {} }) => timerExtra
)

export const patientCaseManagers$ = createSelector(
  common,
  ({ patientCaseManagers = [] }) =>
    patientCaseManagers.map((item, key) => ({
      ...item,
      key,
      disabled: item.availableEmployees <= 0,
      value: item.uuid,
      title: item.fullName
    }))
)

export const actionCounter$ = createSelector(common, ({ actionCounter }) =>
  Array.isArray(actionCounter) ? actionCounter : []
)

export const taskCounter$ = createSelector(
  actionCounter$,
  (actionCounter = []) => {
    const taskCounter =
      actionCounter?.find(({ actionType }) => actionType === 'task')?.actions ||
      0

    return taskCounter
  }
)

export const careGapCounter$ = createSelector(
  actionCounter$,
  (actionCounter = []) => {
    const careGapCounter =
      actionCounter?.find(({ actionType }) => actionType === 'careGap')
        ?.actions || 0

    return careGapCounter
  }
)

export const notificationCounter$ = createSelector(
  actionCounter$,
  (actionCounter = []) => {
    const notificationCounter =
      actionCounter?.find(({ actionType }) => actionType === 'notification')
        ?.actions || 0

    return notificationCounter
  }
)

export const referenceTableShowInactive = createSelector(
  common,
  ({ referenceTableShowInactive = false }) => referenceTableShowInactive
)
